import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { DraftingCompass, Shield, Clock, ArrowRight, ChevronDown, ChevronUp, MessageSquare, PenTool, Cog, Layers, CheckCircle2, Repeat2, Plus } from 'lucide-react';
import TrustBenefitsSection from '../components/TrustBenefitsSection';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.45, ease: 'easeOut' } })
};

const services = [
  { name: 'From Scratch', desc: 'No drawings, sketches, or models? No problem. Describe your idea and we will design it from the ground up.', icon: PenTool },
  { name: 'From a Sketch', desc: 'Hand drawings, napkin sketches, or photos; we turn concepts into precise 3D models.', icon: DraftingCompass },
  { name: 'Modify Existing', desc: 'Need changes to an existing design? We can modify, optimize, or adapt your current models.', icon: Cog },
  { name: 'Reverse Engineer', desc: 'Have a physical part but no CAD? We can recreate it digitally and provide CAD 3D models.', icon: Layers },
];

const faqs = [
  { q: 'Do I own the design?', a: 'Yes, 100%. You retain full intellectual property rights to all designs we create for you. We never reuse or share client designs.' },
  { q: 'What if I only have a rough idea?', a: 'That\'s perfectly fine. Many of our projects start with just a description or a rough sketch. Our engineers will work with you to refine the concept into a precise, manufacturable 3D model.' },
  { q: 'How long does a custom design take?', a: 'Simple parts can be designed in 2-4 days. Complex assemblies or multi-part projects typically take 1-2 weeks. We\'ll provide an estimated timeline with your quote.' },
  { q: 'Can you design parts that need to fit with existing components?', a: 'Absolutely. We regularly design parts that interface with existing hardware. Provide us with dimensions, 3D scans, or reference parts required to fit and we\'ll ensure proper fitment of the new part.' },
  { q: 'What file formats will I receive?', a: 'You\'ll receive your design in industry-standard formats including STEP, STL, 3MF, and others including the native CAD file format. These are suitable for 3D printing or other manufacturing and fabrication methods.' },
];

const FAQ = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left group">
        <span className="font-medium text-slate-900 group-hover:text-[#0007b5] transition-colors pr-4">{q}</span>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />}
      </button>
      {open && <p className="pb-5 text-slate-600 text-sm leading-relaxed">{a}</p>}
    </div>
  );
};

const ServiceRow = ({ svc }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-slate-200 last:border-b">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-6 py-7 lg:py-9 text-left group"
        data-testid={`service-row-${svc.name.replace(/\s+/g, '-').toLowerCase()}`}
        aria-expanded={open}
      >
        <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight transition-colors duration-300 group-hover:text-[#0007b5]">
          {svc.name}
        </h3>
        <span
          className={`w-12 h-12 lg:w-14 lg:h-14 rounded-full border flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
            open
              ? 'bg-[#0007b5] border-[#0007b5] rotate-45'
              : 'border-slate-300 group-hover:border-[#0007b5] group-hover:bg-[#0007b5]'
          }`}
          aria-hidden="true"
        >
          <Plus className={`w-5 h-5 lg:w-6 lg:h-6 transition-colors duration-300 ${
            open ? 'text-white' : 'text-slate-500 group-hover:text-white'
          }`} strokeWidth={2.25} />
        </span>
      </button>
      <div className={`grid transition-all duration-400 ease-out ${open ? 'grid-rows-[1fr] opacity-100 pb-8 lg:pb-10' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <p className="text-base lg:text-lg text-slate-600 leading-relaxed max-w-3xl">
            {svc.desc}
          </p>
        </div>
      </div>
    </div>
  );
};

const CustomDesignLanding = () => {
  const navigate = useNavigate();
  const goToTool = () => navigate('/get-started?service=design');

  const svgUrl = "https://customer-assets.emergentagent.com/job_4d5d21e8-e723-4374-8b92-bc54b14f6d43/artifacts/mltj7u8x_PETG-CF%20Functional%20Bracket%202.jpg";

  return (
    <div className="min-h-screen bg-white">
      {/* Minimal Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
          <a href="/" className="flex items-center flex-shrink-0" data-testid="landing-logo">
            <img src="/logo-nav.png" alt="Create 3D Works" className="h-7 sm:h-8 object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/faq" className="text-sm text-slate-600 hover:text-[#0007b5] transition-colors font-medium">FAQ</a>
            <a href="/contact" className="text-sm text-slate-600 hover:text-[#0007b5] transition-colors font-medium">Contact</a>
          </nav>
          <button data-testid="header-cta" onClick={goToTool}
            className="px-3 sm:px-6 py-2 sm:py-2.5 bg-[#0007b5] text-white text-sm sm:text-base font-semibold rounded-lg hover:bg-[#00058F] transition-colors flex items-center gap-1.5 sm:gap-2 shadow-md shadow-blue-500/20 flex-shrink-0">
            <img src="https://customer-assets.emergentagent.com/job_801805a1-56d6-4a1b-9dc4-a3c271402139/artifacts/2uma28ub_Asset%205%401x.png" alt="" className="w-4 h-4 sm:w-5 sm:h-5 object-contain" />
            <span className="whitespace-nowrap">Start a Project</span>
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-white pt-16 pb-20 lg:pt-20 lg:pb-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-12 gap-8 lg:gap-4 items-center">
            <motion.div className="col-span-12 lg:col-span-7" initial="hidden" animate="visible" variants={fadeUp}>
              <p className="text-[#0007b5] text-sm font-semibold uppercase tracking-widest mb-5">Custom 3D Design Services</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-5">
                We design your<br />parts for you
              </h1>
              <p className="text-lg text-slate-500 max-w-xl mb-10 leading-relaxed">
                From an idea to a production-ready 3D model, including prototyping and testing. Our engineers take projects from concept to reality.
              </p>
              <button data-testid="hero-cta" onClick={goToTool} className="inline-flex items-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 bg-[#0007b5] text-white rounded-xl font-bold text-base sm:text-lg hover:bg-[#00058F] transition-colors shadow-lg shadow-blue-500/20">
                Start Your Design Project <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-6 text-xs text-slate-400">
                <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Full IP ownership</span>
                <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Fast turnaround</span>
                <span className="flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Dedicated engineer</span>
              </div>
            </motion.div>
            <motion.div className="col-span-12 lg:col-span-5 hidden lg:block" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <img src={svgUrl} alt="PETG-CF functional 3D-printed bracket" className="w-full h-auto object-cover rounded-2xl scale-100 lg:scale-[1.15] shadow-2xl shadow-blue-500/30" loading="eager" />
            </motion.div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-[#0007b5] via-blue-400 to-[#0007b5]" />
      </section>

      {/* Trust & Benefits — editorial, typography-led */}
      <TrustBenefitsSection
        testId="custom-design-trust"
        eyebrow="Real engineers, real conversations"
        marker="check"
        headline={<>Talk directly to<br />the engineer<br />designing your part.</>}
        lead={[
          "Custom design works best with real collaboration; not a one-time input, not a junior intern, and not a chatbot.",
          "Every project is personally owned by a designer and a senior engineer, who take it from your first sketch all the way to final production-ready STEP files.",
          "You\u2019ll always know who\u2019s working on your design, how to reach them directly, and exactly where your project stands at every milestone.",
        ]}
        benefits={[
          {
            title: 'A small team that knows your project',
            desc: "You'll work with a tight crew, not a rotating cast of account managers. The person who scopes your project is the same person designing it and answering your questions along the way.",
          },
          {
            title: 'Direct conversations with your designer',
            desc: "Whether it's a quick clarification, a fitment concern, or a late-stage revision, message your engineer directly. No tickets, no triage, no waiting days for an answer that should take minutes.",
          },
          {
            title: 'Designed and prototyped locally',
            desc: "Every CAD file is created in-house in North America, and prototypes are printed on the same floor. That means your ideas and products are not shared with anyone, tighter feedback loops, faster iteration, and no waiting weeks for an overseas revision.",
          },
          {
            title: 'Secure payments powered by Stripe',
            desc: "Payments are secure and flexible. Choose Stripe (PCI-compliant, fraud-protected, trusted by millions) or simple bank transfer. Your card data never touches our servers.",
          },
          {
            title: 'Your satisfaction is the deliverable',
            desc: "We revise until the design is right. If a part doesn't fit, doesn't function, or doesn't match the brief, we fix it on us. Your requirements are what closes the project, not our internal checklist.",
          },
        ]}
      />

      {/* Trust Marquee */}
      <section className="py-8 border-y border-slate-100 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-xs font-semibold text-slate-400 uppercase tracking-widest mb-6">Custom designs for every industry</p>
          <Marquee speed={40} gradient gradientColor="#f8fafc" gradientWidth={80} pauseOnHover>
            {['Automotive', 'Drone Parts', 'Jigs & Fixtures', 'Consumer Products', 'Sim Racing', 'Enclosures', 'Robotics'].map((item) => (
              <span key={item} className="mx-12 text-slate-400 font-semibold text-lg">{item}</span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp}>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight">How it works</h2>
          </motion.div>

          {/* Steps 1-3 on one row, step 4 as a completion banner below */}
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { step: '1', title: 'Describe', desc: 'Tell us about your project: upload sketches, reference images, or simply describe your idea.' },
                { step: '2', title: 'Design', desc: 'Our engineers create your 3D model with proper dimensions, tolerances, and manufacturing considerations.' },
                { step: '3', title: 'Test', desc: 'We produce functional prototypes, validate fit and performance, and iterate until the design is proven.' },
              ].map((item, i) => (
                <motion.div
                  key={item.step}
                  className="relative bg-white border border-slate-200 rounded-2xl p-8 pt-20 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                  initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={fadeUp} custom={i}
                >
                  <div className="text-5xl sm:text-6xl lg:text-7xl font-bold text-slate-100 absolute top-4 right-6">{item.step}</div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>

            {/* Completion step — full-width brand blue banner */}
            <motion.div
              className="relative bg-[#0007b5] rounded-2xl px-10 py-16 overflow-hidden shadow-xl shadow-blue-500/20"
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={fadeUp} custom={3}
            >
              <div className="text-[8rem] font-bold text-white/10 absolute top-0 right-8 leading-none select-none pointer-events-none">4</div>
              <div className="relative flex flex-col md:flex-row md:items-start gap-6 md:gap-8">
                <div className="w-16 h-16 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-9 h-9 text-white" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-blue-200 uppercase tracking-widest mb-2">Project Complete</p>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">Deliver</h3>
                  <p className="text-blue-100 leading-relaxed max-w-2xl">Receive your production-ready CAD files in STEP, STL, and native formats. Request revisions as needed.</p>
                </div>
                <button
                  onClick={goToTool}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#0007b5] rounded-xl font-bold hover:bg-blue-50 transition-colors flex-shrink-0 md:mt-[4.25rem]"
                >
                  Start Your Project <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats band — three-column centered */}
      <section className="py-16 bg-slate-50 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-10">
            <motion.div
              className="flex items-center justify-center gap-5"
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={fadeUp} custom={0}
            >
              <Shield className="w-10 h-10 text-[#0007b5] flex-shrink-0" strokeWidth={1.75} />
              <div className="max-w-[220px]">
                <p className="text-xl font-bold text-slate-900">You maintain full IP</p>
                <p className="text-sm text-slate-500 mt-1">Your files are never shared with third parties. Your IP is always yours.</p>
              </div>
            </motion.div>
            <motion.div
              className="flex items-center justify-center gap-5"
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={fadeUp} custom={1}
            >
              <MessageSquare className="w-10 h-10 text-[#0007b5] flex-shrink-0" strokeWidth={1.75} />
              <div className="max-w-[220px]">
                <p className="text-xl font-bold text-slate-900">Dedicated support</p>
                <p className="text-sm text-slate-500 mt-1">Work directly with an engineer throughout your project</p>
              </div>
            </motion.div>
            <motion.div
              className="flex items-center justify-center gap-5"
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={fadeUp} custom={2}
            >
              <Repeat2 className="w-10 h-10 text-[#0007b5] flex-shrink-0" strokeWidth={1.75} />
              <div className="max-w-[220px]">
                <p className="text-xl font-bold text-slate-900">Design revisions</p>
                <p className="text-sm text-slate-500 mt-1">Revise and update until specs and requirements are met</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Can Do */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Centered title — full page width */}
          <motion.div
            className="text-center mb-14 lg:mb-16"
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp}
          >
            <p className="text-[#0007b5] text-sm font-semibold uppercase tracking-widest mb-4">Take the guesswork out</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.05] mb-5">
              What we can do
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
              No matter where you are in the process — a rough idea, an existing part, or something in between — we can help.
            </p>
          </motion.div>

          {/* Full-width collapsible service rows */}
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={fadeUp}
            data-testid="custom-design-services-list"
          >
            {services.map((svc) => (
              <ServiceRow key={svc.name} svc={svc} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Create 3D Works */}
      <section className="py-20 lg:py-24 bg-slate-50 border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="flex flex-col items-center mb-8"
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 flex items-center justify-center gap-3 flex-wrap">
              Why <img src="https://customer-assets.emergentagent.com/job_4d5d21e8-e723-4374-8b92-bc54b14f6d43/artifacts/kg4derrn_Asset%202%403x%20%28text%29.png" alt="CREATE 3D Works" className="h-[72px] inline-block" />
            </h2>
            <div className="w-40 h-0.5 bg-[#0007b5] mt-4" />
          </motion.div>
          <motion.p
            className="text-lg text-slate-600 leading-relaxed mb-10 text-center"
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp}
          >
            Our expertise spans every stage of 3D production—from design and prototyping to testing, revisions, and finishing—equipping us to support your project needs at every stage. We understand how each phase integrates into the whole process, providing seamless, informed solutions that reduce risk and improve efficiency for your projects.
          </motion.p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: 'Fast Turnaround Times', desc: 'Competitive pricing without compromising on quality or speed' },
              { title: 'Expert Support', desc: 'Guidance throughout your entire project lifecycle' },
              { title: 'Confidential Projects', desc: 'Secure handling with NDA protection for sensitive work' },
              { title: 'Real-World Experience', desc: '3,000+ lb. of production across a broad-spectrum of applications and use cases' },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                className="flex items-start"
                initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={fadeUp} custom={i}
              >
                <CheckCircle2 className="w-6 h-6 text-green-500 mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-slate-600">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            className="text-3xl sm:text-4xl font-bold text-slate-900 text-center tracking-tight mb-10"
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp}
          >
            Frequently asked questions
          </motion.h2>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={fadeUp}>
            {faqs.map((faq) => <FAQ key={faq.q} q={faq.q} a={faq.a} />)}
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-24 bg-[#0007b5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">Have an idea? Let's bring it to life.</h2>
            <p className="text-lg text-white/70 mb-8">Tell us about your project and our engineers will take it from there.</p>
            <button
              data-testid="cta-bottom"
              onClick={goToTool}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0007b5] rounded-xl font-bold text-lg hover:bg-slate-100 transition-colors shadow-lg"
            >
              Start Your Design Project <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-sm text-slate-500">&copy; {new Date().getFullYear()} C3D Works. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <a href="/privacy-policy" className="text-sm text-slate-500 hover:text-[#0007b5] transition-colors">Privacy</a>
              <a href="/terms-of-service" className="text-sm text-slate-500 hover:text-[#0007b5] transition-colors">Terms</a>
              <a href="/contact" className="text-sm text-slate-500 hover:text-[#0007b5] transition-colors">Contact</a>
              <a href="/material-guide" className="text-sm text-slate-500 hover:text-[#0007b5] transition-colors">Materials</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CustomDesignLanding;
